<template>
  <v-card flat color="transparent" class="bookmark-item--headline d-flex align-start">
    <BulkSelectCheckbox
      v-if="isBulkActionsActive"
      class="mx-3 mt-0"
      :checked="isSelected"
      @click="toggleSelect"
    />
    <div class="full-width">
      <div class="text-neutral400 d-flex align-center mb-2 text-caption">
        <BookmarkSource :bookmark="bookmark" />
        <span class="mx-1">&middot;</span>
        <BookmarkDate :value="bookmark.createdAt" />
      </div>

      <div
        class="bookmark-headline pa-0 mb-3 text-body-1 font-weight-semibold text-neutral900 d-flex align-center cursor-pointer"
        @click="onContentClick"
      >
        <v-progress-circular
          v-if="bookmark.isLoading && !bookmark.hasSufficientData"
          indeterminate size="16" color="blue400" width="3"
          class="mr-2"
        />
        <span class="title-text" v-text="bookmark.title ?? bookmark.domain" />
        <BrandButton
          v-if="!isBulkActionsActive"
          class="outgoing-link-btn ml-2 flex-shrink-0"
          size="x-small"
          icon
          flat
          :title="t(bookmark.isImage ? 'cta.download_original' : 'cta.view_original_content')"
          @click.stop.prevent="openSource"
        >
          <PhArrowSquareOut class="text-body-1 text-neutral600" />
        </BrandButton>
      </div>
      <BookmarkTags v-if="bookmark.tags.length" class="mb-3" :tags="bookmark.tags" @tag-selected="onTagSelected" />

      <BookmarkActions
        :bookmark="bookmark" :user-id="userId"
        :public-mode="publicMode"
        :membership="membership"
        class="text-text"
        @favorite="$emit('favorite')"
        @save="$emit('save')"
        @add-to-collection="$emit('add-to-collection')"
        @add-to-collection-from-collection="$emit('add-to-collection-from-collection')"
        @comments="$emit('comments')"
        @like="$emit('like')"
        @likes="$emit('likes')"
        @pin-edit="$emit('pin-edit')"
      />
    </div>
  </v-card>
</template>

<script setup>
import { computed, defineProps, defineEmits } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { PhArrowSquareOut } from '@phosphor-icons/vue'
import BookmarkSource from './BookmarkSource'
import BookmarkActions from './BookmarkActions'
import BookmarkDate from './BookmarkDate'
import BookmarkTags from './BookmarkTags'
import BrandButton from '@/components/global/brand/BrandButton.vue'
import { useBulkActionsV2 } from '@/composables/useBulkActionsV2'
import { useBookmarkItemSourceUrl } from '@/composables/useBookmarkSourceUrl'
import BulkSelectCheckbox from '@/components/BulkSelectCheckbox.vue'

const props = defineProps({
  bookmark: {
    type: Object,
    required: true,
  },
  userId: {
    type: String,
    default: null,
  },
  membership: {
    type: Object,
    default: null,
  },
  publicMode: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits([
  'toggle-select',
  'tag-selected',
  'favorite',
  'save',
  'add-to-collection',
  'add-to-collection-from-collection',
  'comments',
  'like',
  'likes',
  'pin-edit',
])

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const { isBulkActionsActive, isItemSelected } = useBulkActionsV2()

const isSelected = computed(() => isItemSelected(props.bookmark.id))

const detailRoute = computed(() => {
  if (props.publicMode) {
    const username = route.params.username
    const cid = route.params.id || props.bookmark.ownerId
    return `/u/${username}/collections/${cid}/bookmarks/${props.bookmark.id}`
  }
  if (props.bookmark.isCollectionBookmark) {
    return `/collections/${props.bookmark.ownerId}/bookmarks/${props.bookmark.id}`
  }
  return `/bookmarks/${props.bookmark.id}`
})

function onContentClick() {
  if (isBulkActionsActive.value) {
    toggleSelect()
  } else {
    router.push(detailRoute.value)
  }
}

const { openSource } = useBookmarkItemSourceUrl(props)

function toggleSelect() {
  emit('toggle-select', props.bookmark)
}

function onTagSelected(tag) {
  emit('tag-selected', tag)
}
</script>

<style lang="scss" scoped>
.bookmark-headline {
  .title-text {
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    display: -webkit-box;
    overflow: hidden;
  }
}

.bookmark-description {
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  display: -webkit-box;
  overflow: hidden;
}
</style>
